import { useState } from "react";
import { StaffActivityLog } from "@/redux/services/pos/pos.type";
import { formatDate, formatPhoneNumber } from "@/lib/utils";
import { ChevronDown, ChevronUp } from "lucide-react";

interface LogEntryCardProps {
  log: StaffActivityLog;
}

const ENTITY_CLASS_MAP: Record<string, string> = {
  "App\\Entity\\Order\\Order": "Commande",
  "App\\Entity\\Menu\\Menu": "Menu",
  "App\\Entity\\Menu\\MenuItem": "Article",
  "App\\Entity\\Menu\\MenuSection": "Section",
  "App\\Entity\\Restaurant\\Restaurant": "Restaurant",
  "App\\Entity\\User": "Utilisateur",
};

function getEntityName(objectClass: string): string {
  if (ENTITY_CLASS_MAP[objectClass]) {
    return ENTITY_CLASS_MAP[objectClass];
  }
  // Fallback: extract last part of the class name
  const parts = objectClass.split("\\");
  return parts[parts.length - 1] ?? objectClass;
}

function getActionLabel(action: string): string {
  switch (action) {
    case "create":
      return "Création";
    case "update":
      return "Modification";
    case "remove":
      return "Suppression";
    default:
      return action;
  }
}

function getActionBadgeClasses(action: string): string {
  switch (action) {
    case "create":
      return "bg-green-100 text-green-700";
    case "update":
      return "bg-blue-100 text-blue-700";
    case "remove":
      return "bg-red-100 text-red-700";
    default:
      return "bg-muted text-card-foreground";
  }
}

function LogEntryCard({ log }: LogEntryCardProps) {
  const [expanded, setExpanded] = useState(false);

  const dataEntries = Object.entries(log.data ?? {});
  const hasData = dataEntries.length > 0;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        onClick={() => hasData && setExpanded(!expanded)}
        className={`w-full p-4 text-left ${hasData ? "cursor-pointer hover:bg-muted" : "cursor-default"} transition-colors`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          {/* Timestamp */}
          <span className="text-xs text-muted-foreground shrink-0">
            {formatDate(new Date(log.loggedAt))}
          </span>

          {/* Staff phone */}
          <span className="text-sm font-medium text-card-foreground shrink-0">
            {formatPhoneNumber(log.username)}
          </span>

          {/* Action badge */}
          <span
            className={`inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getActionBadgeClasses(log.action)}`}
          >
            {getActionLabel(log.action)}
          </span>

          {/* Entity info */}
          <span className="text-sm text-muted-foreground">
            {/* Entity type only: never show a UUID fragment (SFRESTO-167). */}
            {getEntityName(log.objectClass)}
          </span>

          {/* Expand indicator */}
          {hasData && (
            <span className="ml-auto shrink-0 text-muted-foreground">
              {expanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </span>
          )}
        </div>
      </button>

      {/* Expanded data */}
      {expanded && hasData && (
        <div className="border-t border-border bg-muted/50 px-4 py-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Données modifiées
          </p>
          <div className="space-y-1">
            {dataEntries.map(([key, value]) => (
              <div
                key={key}
                className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2 text-sm"
              >
                <span className="font-medium text-muted-foreground shrink-0 font-mono text-xs">
                  {key}:
                </span>
                <span className="text-card-foreground break-all text-xs">
                  {typeof value === "object"
                    ? JSON.stringify(value, null, 2)
                    : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default LogEntryCard;
